import { useState, useEffect } from "react";
import axios from "axios";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { 
  Plane, Clock, CheckCircle, Star, Phone, Mail, MapPin, 
  ArrowRight, ChevronDown, Menu, X, Shield, Users, Car
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Calendar } from "../components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "../components/ui/popover";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "../components/ui/accordion";
import { format } from "date-fns";
import { it, enUS } from "date-fns/locale";
import SEO from "../components/SEO";
import LanguageSwitcher from "../components/LanguageSwitcher";
import { trackEvent } from "../lib/analytics";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const WHATSAPP_NUMBER = "+393283796150";
const WHATSAPP_LINK = `https://wa.me/${WHATSAPP_NUMBER.replace(/[^0-9]/g, '')}`;

const IMAGES = {
  hero: "https://images.squarespace-cdn.com/content/v1/690f381fe8f7b37dcf97958b/b72b31f6-5a0f-49af-b8ff-83591c95fc1c/ChatGPT+Image+19+nov+2025%2C+16_55_51.png",
  logo: "/black-arrow-logo.png",
  mercedes: "https://customer-assets.emergentagent.com/job_09b7cb9d-9c9c-4ed4-b151-2318db7aea34/artifacts/9clppwhp_Business_Class_Ncc_LVF8682_1-1024x683.jpg"
};

// Navigation Component
const Navigation = () => {
  const { t } = useTranslation();
  const [scrolled, setScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  return (
    <nav 
      data-testid="naples-navigation"
      className={`fixed top-0 w-full z-50 transition-all duration-300 ${
        scrolled ? "glassmorphism py-3" : "bg-transparent py-5"
      }`}
    >
      <div className="container-custom flex items-center justify-between">
        <a href="/" className="flex items-center gap-3" data-testid="naples-logo">
          <img src={IMAGES.logo} alt="Black Arrow NCC" className="h-16 w-auto object-contain" />
          <span className="font-serif text-xl text-white tracking-wide">Black Arrow NCC</span>
        </a>

        <div className="hidden lg:flex items-center gap-8">
          <a href="/" className="nav-link text-sm uppercase tracking-widest font-medium">{t("nav.back_home")}</a>
          <button onClick={() => document.getElementById("prenota")?.scrollIntoView({ behavior: "smooth" })} className="nav-link text-sm uppercase tracking-widest font-medium">
            {t("nav.book_now")}
          </button>
          <button onClick={() => document.getElementById("faq")?.scrollIntoView({ behavior: "smooth" })} className="nav-link text-sm uppercase tracking-widest font-medium">
            FAQ
          </button>
        </div>

        <div className="hidden lg:flex items-center gap-4">
          <LanguageSwitcher />
          <a href={`tel:${WHATSAPP_NUMBER}`} className="text-white hover:text-gold transition-colors">
            <Phone className="h-5 w-5" />
          </a>
          <Button 
            onClick={() => { trackEvent("cta_click", { cta: "prenota_transfer", location: "naples_header" }); document.getElementById("prenota")?.scrollIntoView({ behavior: "smooth" }); }}
            className="btn-gold px-6 py-2 font-medium"
            data-testid="naples-header-book-btn"
          >
            {t("nav.book_now")}
          </Button>
        </div>

        <div className="lg:hidden flex items-center gap-3">
          <LanguageSwitcher variant="mobile" />
          <button className="text-white" onClick={() => setMobileMenuOpen(!mobileMenuOpen)}>
            {mobileMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
          </button>
        </div>
      </div>

      {mobileMenuOpen && (
        <div className="lg:hidden absolute top-full left-0 w-full bg-black/95 backdrop-blur-lg border-t border-white/10 py-6">
          <div className="container-custom flex flex-col gap-4">
            <a href="/" className="text-white py-2 uppercase tracking-widest text-sm">{t("nav.back_home")}</a>
            <button onClick={() => { document.getElementById("prenota")?.scrollIntoView({ behavior: "smooth" }); setMobileMenuOpen(false); }} className="text-left text-white py-2 uppercase tracking-widest text-sm">
              {t("nav.book_now")}
            </button>
            <button onClick={() => { document.getElementById("faq")?.scrollIntoView({ behavior: "smooth" }); setMobileMenuOpen(false); }} className="text-left text-white py-2 uppercase tracking-widest text-sm">
              FAQ
            </button>
          </div>
        </div>
      )}
    </nav>
  );
};

// Hero Section
const HeroSection = () => {
  const { t } = useTranslation();
  return (
    <section className="hero-section" data-testid="naples-hero-section">
      <div className="hero-bg" style={{ backgroundImage: `url(${IMAGES.hero})` }} />
      <div className="absolute inset-0 hero-overlay" />
      
      <div className="hero-content max-w-4xl mx-auto">
        <div className="inline-flex items-center gap-2 bg-gold/20 border border-gold/30 px-4 py-2 mb-6">
          <Plane className="h-4 w-4 text-gold" />
          <span className="text-gold text-sm uppercase tracking-widest">{t("naples.hero.eyebrow")}</span>
        </div>
        
        <h1 className="font-serif text-4xl sm:text-5xl lg:text-7xl text-white mb-6 leading-tight">
          {t("naples.hero.title_l1")}<br />
          <span className="gold-text">{t("naples.hero.title_l2")}</span>
        </h1>
        <p className="text-muted-foreground text-lg md:text-xl mb-10 max-w-2xl mx-auto">
          {t("naples.hero.desc")}
        </p>
        <div className="flex flex-col sm:flex-row gap-4 justify-center">
          <Button 
            onClick={() => { trackEvent("cta_click", { cta: "prenota_transfer", location: "naples_hero" }); document.getElementById("prenota")?.scrollIntoView({ behavior: "smooth" }); }}
            className="btn-gold px-10 py-6 text-lg font-medium"
            data-testid="naples-hero-book-btn"
          >
            {t("naples.hero.cta_book")}
            <ArrowRight className="ml-2 h-5 w-5" />
          </Button>
          <a 
            href={WHATSAPP_LINK}
            target="_blank"
            rel="noopener noreferrer"
            onClick={() => trackEvent("whatsapp_click", { location: "naples_hero" })}
            className="btn-outline-gold px-10 py-6 text-lg font-medium inline-flex items-center justify-center gap-2"
            data-testid="naples-hero-whatsapp"
          >
            <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 24 24">
              <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
            </svg>
            {t("hero.cta_whatsapp")}
          </a>
        </div>
      </div>

      <div className="absolute bottom-8 left-1/2 -translate-x-1/2 animate-bounce">
        <ChevronDown className="h-8 w-8 text-gold/50" />
      </div>
    </section>
  );
};

// Routes Section
const RoutesSection = () => {
  const { t } = useTranslation();
  const routes = [
    { key: "sorrento", time: "50 min" },
    { key: "positano", time: "1h 15min" },
    { key: "amalfi", time: "1h 30min" },
    { key: "ravello", time: "1h 45min" },
    { key: "centro", time: "20 min" },
    { key: "pompei", time: "30 min" }
  ];

  return (
    <section className="section bg-luxe-dark" data-testid="routes-section">
      <div className="container-custom">
        <div className="text-center mb-16">
          <p className="text-gold uppercase tracking-[0.2em] text-sm mb-4">{t("naples.routes.eyebrow")}</p>
          <h2 className="font-serif text-3xl md:text-5xl text-white">
            {t("naples.routes.title")}
          </h2>
        </div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {routes.map((route) => (
            <div key={route.key} className="service-card p-6 flex justify-between items-center" data-testid={`route-${route.key}`}>
            <div>
              <h3 className="font-serif text-xl text-white mb-2">{t(`naples.routes.items.${route.key}`)}</h3>
              <div className="flex items-center gap-2 text-muted-foreground">
                <Clock className="h-4 w-4 text-gold" />
                <span>{route.time}</span>
              </div>
            </div>
            <ArrowRight className="h-5 w-5 text-gold/60 shrink-0" />
          </div>
          ))}
        </div>

        {/* Prezzo su richiesta banner */}
        <div
          className="mt-14 bg-gradient-to-br from-[#0A0A0A] to-[#050505] border border-gold/40 p-8 md:p-10 text-center"
          data-testid="routes-quote-banner"
        >
          <p className="text-gold uppercase tracking-[0.3em] text-xs mb-3">{t("naples.routes.banner.eyebrow")}</p>
          <h3 className="font-serif text-2xl md:text-3xl text-white mb-3">
            {t("naples.routes.banner.title")}
          </h3>
          <p className="text-muted-foreground max-w-xl mx-auto mb-8 leading-relaxed">
            {t("naples.routes.banner.desc")}
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a
              href={WHATSAPP_LINK}
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => trackEvent("whatsapp_click", { location: "naples_quote_banner" })}
              className="btn-gold px-8 py-4 inline-flex items-center justify-center gap-2 font-medium"
              data-testid="routes-whatsapp-btn"
            >
              <svg className="h-5 w-5" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
              </svg>
              {t("naples.routes.banner.whatsapp")}
            </a>
            <a
              href={`tel:${WHATSAPP_NUMBER}`}
              onClick={() => trackEvent("phone_click", { location: "naples_quote_banner" })}
              className="btn-outline-gold px-8 py-4 inline-flex items-center justify-center gap-2 font-medium"
              data-testid="routes-call-btn"
            >
              <Phone className="h-5 w-5" />
              {t("naples.routes.banner.call")}
            </a>
          </div>
          <p className="text-muted-foreground text-sm mt-6">
            {t("naples.routes.banner.note")}
          </p>
        </div>
      </div>
    </section>
  );
};

// Why Choose Section
const WhyChooseSection = () => {
  const { t } = useTranslation();
  const features = [
    { icon: Plane, key: "flight" },
    { icon: Users, key: "meet" },
    { icon: Shield, key: "fixed" },
    { icon: Car, key: "fleet" }
  ];

  return (
    <section className="section bg-luxe-charcoal" data-testid="naples-why-section">
      <div className="container-custom">
        <div className="grid lg:grid-cols-2 gap-16 items-center">
          <div>
            <p className="text-gold uppercase tracking-[0.2em] text-sm mb-4">{t("naples.why.eyebrow")}</p>
            <h2 className="font-serif text-3xl md:text-5xl text-white mb-8">
              {t("naples.why.title")}
            </h2>

            <div className="grid sm:grid-cols-2 gap-6">
              {features.map((feature) => (
                <div key={feature.key} className="flex items-start gap-4">
                  <div className="w-10 h-10 bg-secondary border border-border flex items-center justify-center flex-shrink-0">
                    <feature.icon className="h-5 w-5 text-gold" />
                  </div>
                  <div>
                    <h4 className="text-white font-medium mb-1">{t(`naples.why.items.${feature.key}.title`)}</h4>
                    <p className="text-muted-foreground text-sm">{t(`naples.why.items.${feature.key}.desc`)}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div>
            <img src={IMAGES.mercedes} alt="Transfer Mercedes di lusso" className="w-full h-[500px] object-cover" />
          </div>
        </div>
      </div>
    </section>
  );
};

// Testimonials Section
const TestimonialsSection = () => {
  const { t, i18n } = useTranslation();
  const [testimonials, setTestimonials] = useState([]);

  useEffect(() => {
    const fetchTestimonials = async () => {
      try {
        const response = await axios.get(`${API}/testimonials`);
        setTestimonials(response.data.testimonials);
      } catch (err) {
        if (process.env.NODE_ENV === "development") {
          console.error("Failed to fetch testimonials:", err);
        }
      }
    };
    fetchTestimonials();
  }, []);

  // Fallback to static translated testimonials when English or when API returns empty
  const staticKeys = ["t1", "t2", "t3"];
  const displayed = (i18n.language === "en" || testimonials.length === 0)
    ? staticKeys.map((k) => ({
        id: k,
        rating: 5,
        text: t(`naples.testimonials.items.${k}.text`),
        name: t(`naples.testimonials.items.${k}.author`),
        location: t(`naples.testimonials.items.${k}.location`),
      }))
    : testimonials;

  return (
    <section className="section bg-luxe-cream" data-testid="naples-testimonials">
      <div className="container-custom">
        <div className="text-center mb-16">
          <p className="text-gold uppercase tracking-[0.2em] text-sm mb-4">{t("naples.testimonials.eyebrow")}</p>
          <h2 className="font-serif text-3xl md:text-5xl text-white">
            {t("naples.testimonials.title")}
          </h2>
        </div>

        <div className="grid md:grid-cols-2 gap-8">
          {displayed.map((testimonial) => (
            <div key={testimonial.id} className="testimonial-card" data-testid={`naples-testimonial-${testimonial.id}`}>
              <div className="flex gap-1 mb-4">
                {[...Array(testimonial.rating)].map((_, i) => (
                  <Star key={i} className="h-5 w-5 fill-gold text-gold" />
                ))}
              </div>
              <p className="text-white text-lg mb-6 italic">{testimonial.text}</p>
              <div>
                <p className="text-gold font-medium">{testimonial.name}</p>
                <p className="text-muted-foreground text-sm">{testimonial.location}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

// Booking Section
const BookingSection = () => {
  const { t, i18n } = useTranslation();
  const dateLocale = i18n.language === "en" ? enUS : it;
  const [loading, setLoading] = useState(false);
  const [date, setDate] = useState(null);
  const [formData, setFormData] = useState({
    name: "",
    phone: "",
    email: "",
    pickup_location: "Aeroporto di Napoli (NAP)",
    destination: "",
    time: "",
    notes: ""
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!date) {
      toast.error(t("booking.form.toast_no_date"));
      return;
    }

    setLoading(true);
    trackEvent("booking_submit_attempt", { page: "naples_airport" });
    try {
      await axios.post(`${API}/bookings`, {
        ...formData,
        date: format(date, "yyyy-MM-dd"),
        service_type: "airport_transfer"
      });
      trackEvent("booking_submit_success", { page: "naples_airport" });
      toast.success(t("booking.form.toast_success"));
      setFormData({ name: "", phone: "", email: "", pickup_location: "Aeroporto di Napoli (NAP)", destination: "", time: "", notes: "" });
      setDate(null);
    } catch (error) {
      trackEvent("booking_submit_error", { page: "naples_airport" });
      toast.error(t("booking.form.toast_error"));
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  return (
    <section id="prenota" className="section bg-luxe-spotlight-right" data-testid="naples-booking-section">
      <div className="container-custom">
        <div className="max-w-3xl mx-auto">
          <div className="text-center mb-12">
            <p className="text-gold uppercase tracking-[0.2em] text-sm mb-4">{t("naples.booking.eyebrow")}</p>
            <h2 className="font-serif text-3xl md:text-5xl text-white mb-4">
              {t("naples.booking.title_l1")} {t("naples.booking.title_l2")}
            </h2>
            <p className="text-muted-foreground">
              {t("naples.booking.desc")}
            </p>
          </div>

          <div className="bg-card border border-border p-8 md:p-10">
            <form onSubmit={handleSubmit} className="space-y-6" data-testid="naples-booking-form">
              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <label className="text-sm text-muted-foreground mb-2 block">{t("booking.form.name")} *</label>
                  <Input name="name" value={formData.name} onChange={handleChange} required className="form-input w-full" placeholder={t("booking.form.name_ph")} data-testid="naples-input-name" />
                </div>
                <div>
                  <label className="text-sm text-muted-foreground mb-2 block">{t("booking.form.phone")} *</label>
                  <Input name="phone" value={formData.phone} onChange={handleChange} required className="form-input w-full" placeholder={t("booking.form.phone_ph")} data-testid="naples-input-phone" />
                </div>
              </div>

              <div>
                <label className="text-sm text-muted-foreground mb-2 block">{t("booking.form.email")} *</label>
                <Input name="email" type="email" value={formData.email} onChange={handleChange} required className="form-input w-full" placeholder={t("booking.form.email_ph")} data-testid="naples-input-email" />
              </div>

              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <label className="text-sm text-muted-foreground mb-2 block">{t("booking.form.pickup")}</label>
                  <Input name="pickup_location" value={formData.pickup_location} onChange={handleChange} className="form-input w-full" data-testid="naples-input-pickup" />
                </div>
                <div>
                  <label className="text-sm text-muted-foreground mb-2 block">{t("booking.form.destination")} *</label>
                  <Input name="destination" value={formData.destination} onChange={handleChange} required className="form-input w-full" placeholder={t("booking.form.destination_ph")} data-testid="naples-input-destination" />
                </div>
              </div>

              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <label className="text-sm text-muted-foreground mb-2 block">{t("booking.form.date")} *</label>
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button variant="outline" className="form-input w-full justify-start text-left font-normal" data-testid="naples-date-picker">
                        {date ? format(date, "d MMMM yyyy", { locale: dateLocale }) : <span className="text-muted-foreground">{t("booking.form.date_ph")}</span>}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0 bg-card border-border" align="start">
                      <Calendar mode="single" selected={date} onSelect={setDate} disabled={(date) => date < new Date()} locale={dateLocale} initialFocus />
                    </PopoverContent>
                  </Popover>
                </div>
                <div>
                  <label className="text-sm text-muted-foreground mb-2 block">{t("booking.form.time")} *</label>
                  <Input name="time" type="time" value={formData.time} onChange={handleChange} required className="form-input w-full" data-testid="naples-input-time" />
                </div>
              </div>

              <div>
                <label className="text-sm text-muted-foreground mb-2 block">{t("booking.form.notes")}</label>
                <Textarea name="notes" value={formData.notes} onChange={handleChange} className="form-input w-full min-h-[100px]" placeholder={t("booking.form.notes_ph")} data-testid="naples-input-notes" />
              </div>

              <Button type="submit" disabled={loading} className="btn-gold w-full py-6 text-lg font-medium" data-testid="naples-submit-booking">
                {loading ? (
                  <span className="flex items-center gap-2"><span className="spinner" />{t("booking.form.submitting")}</span>
                ) : (
                  <>{t("booking.form.submit")} <ArrowRight className="ml-2 h-5 w-5" /></>
                )}
              </Button>
            </form>
          </div>

          <div className="flex flex-col sm:flex-row justify-center gap-6 mt-10">
            <a 
              href={WHATSAPP_LINK}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center justify-center gap-3 bg-[#25D366] text-white px-8 py-4 font-medium hover:bg-[#1da851] transition-colors"
              data-testid="naples-whatsapp-cta"
            >
              <svg className="h-6 w-6" fill="currentColor" viewBox="0 0 24 24">
                <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
              </svg>
              Richiedi Preventivo su WhatsApp
            </a>
            <a 
              href={`tel:${WHATSAPP_NUMBER}`}
              className="inline-flex items-center justify-center gap-3 border border-gold text-gold px-8 py-4 font-medium hover:bg-gold hover:text-black transition-colors"
              data-testid="naples-call-cta"
            >
              <Phone className="h-5 w-5" />
              +39 328 379 6150
            </a>
          </div>
        </div>
      </div>
    </section>
  );
};

// FAQ Section
const FAQSection = () => {
  const { t } = useTranslation();
  const faqs = ["q1", "q2", "q3", "q4", "q5"];

  return (
    <section id="faq" className="section bg-luxe-charcoal" data-testid="naples-faq-section">
      <div className="container-custom">
        <div className="max-w-3xl mx-auto">
          <div className="text-center mb-12">
            <p className="text-gold uppercase tracking-[0.2em] text-sm mb-4">{t("naples.faq.eyebrow")}</p>
            <h2 className="font-serif text-3xl md:text-5xl text-white">
              {t("naples.faq.title")}
            </h2>
          </div>

          <Accordion type="single" collapsible className="space-y-4">
            {faqs.map((k, index) => (
              <AccordionItem key={k} value={`item-${index}`} className="bg-card border border-border px-6" data-testid={`faq-${k}`}>
                <AccordionTrigger className="text-white text-left hover:text-gold py-6">
                  {t(`naples.faq.items.${k}.q`)}
                </AccordionTrigger>
                <AccordionContent className="text-muted-foreground pb-6">
                  {t(`naples.faq.items.${k}.a`)}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </div>
    </section>
  );
};

// Footer
const Footer = () => {
  const { t } = useTranslation();
  return (
    <footer className="bg-luxe-dark border-t border-gold/20 py-16" data-testid="naples-footer">
      <div className="container-custom">
        <div className="grid md:grid-cols-3 gap-12">
          <div>
            <div className="flex items-center gap-3 mb-6">
              <img src={IMAGES.logo} alt="Black Arrow NCC" className="h-12 w-12 object-contain" />
              <span className="font-serif text-2xl text-white">Black Arrow NCC</span>
            </div>
            <p className="text-muted-foreground leading-relaxed">
              {t("footer.tagline")}
            </p>
          </div>

          <div>
            <h4 className="font-serif text-lg text-gold mb-6">{t("naples.routes.eyebrow")}</h4>
            <ul className="space-y-3 text-muted-foreground">
              <li>{t("naples.routes.items.sorrento")}</li>
              <li>{t("naples.routes.items.positano")}</li>
              <li>{t("naples.routes.items.amalfi")}</li>
              <li>Aeroporto Napoli → Ravello</li>
              <li>Aeroporto Napoli → Pompei</li>
            </ul>
          </div>

          <div>
            <h4 className="font-serif text-lg text-gold mb-6">Contatti</h4>
            <ul className="space-y-3 text-muted-foreground">
              <li className="flex items-center gap-2">
                <Phone className="h-4 w-4 text-gold" />
                <a href={`tel:${WHATSAPP_NUMBER}`} className="hover:text-gold transition-colors">+39 328 379 6150</a>
              </li>
              <li className="flex items-center gap-2">
                <Mail className="h-4 w-4 text-gold" />
                <a href="mailto:blackarrow25@libero.it" className="hover:text-gold transition-colors">blackarrow25@libero.it</a>
              </li>
              <li className="flex items-start gap-2">
                <MapPin className="h-4 w-4 text-gold mt-1" />
                <span>Viale Libertà 55, Paduli (BN)</span>
              </li>
            </ul>
          </div>
        </div>

        <div className="section-divider my-10" />

        {/* PNRR / EU Funding Disclosure */}
        <div className="mb-8 flex justify-end" data-testid="pnrr-badge">
          <div className="bg-white py-2 px-3 inline-flex">
            <img
              src="/pnrr-eu-mic.png"
              alt="Finanziato dall'Unione europea NextGenerationEU — Ministero della Cultura"
              className="h-12 md:h-14 w-auto object-contain"
            />
          </div>
        </div>

        <div className="flex flex-col md:flex-row justify-between items-center gap-4">
          <p className="text-muted-foreground text-sm">
            © {new Date().getFullYear()} Black Arrow NCC. {t("footer.rights")}
          </p>
          <div className="flex flex-wrap gap-x-6 gap-y-2 text-sm text-muted-foreground justify-center md:justify-end">
            <a href="/" className="hover:text-gold transition-colors">{t("nav.back_home")}</a>
            <button type="button" onClick={() => window.dispatchEvent(new Event("open-privacy"))} className="hover:text-gold transition-colors" data-testid="footer-privacy-link">{t("footer.privacy")}</button>
            <button type="button" onClick={() => window.dispatchEvent(new Event("open-cookie-policy"))} className="hover:text-gold transition-colors" data-testid="footer-cookie-link">{t("footer.cookie")}</button>
            <button type="button" onClick={() => window.dispatchEvent(new Event("open-cookie-preferences"))} className="hover:text-gold transition-colors" data-testid="footer-cookie-prefs-link">{t("footer.manage_cookies")}</button>
          </div>
        </div>
      </div>
    </footer>
  );
};

// WhatsApp Floating Button
const WhatsAppFloat = () => {
  return (
    <a href={WHATSAPP_LINK} target="_blank" rel="noopener noreferrer" className="whatsapp-float" data-testid="naples-whatsapp-float" aria-label="Chatta su WhatsApp">
      <svg className="h-7 w-7 text-white" fill="currentColor" viewBox="0 0 24 24">
        <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
      </svg>
    </a>
  );
};

// Main Naples Airport Page Component
const NaplesAirportPage = () => {
  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "Service",
    "serviceType": "Transfer Aeroporto Napoli Capodichino",
    "provider": {
      "@type": "LocalBusiness",
      "name": "Black Arrow NCC",
      "telephone": "+39 328 379 6150",
      "url": "https://blackarrowncc.it/"
    },
    "areaServed": [
      { "@type": "City", "name": "Napoli" },
      { "@type": "City", "name": "Sorrento" },
      { "@type": "City", "name": "Positano" },
      { "@type": "City", "name": "Amalfi" }
    ],
    "description": "Transfer premium da e per l'Aeroporto Internazionale di Napoli Capodichino con monitoraggio volo, meet & greet e flotta Mercedes."
  };

  return (
    <div className="scroll-smooth">
      <SEO
        title="Transfer Aeroporto Napoli Capodichino | Black Arrow NCC"
        description="Transfer privato da e per l'Aeroporto di Napoli Capodichino. Prezzi fissi, monitoraggio volo, meet & greet, 60 min attesa gratuita. Mercedes CLA + Ford Tourneo 9 posti."
        canonical="https://blackarrowncc.it/transfer-aeroporto-napoli"
        jsonLd={jsonLd}
      />
      <Navigation />
      <HeroSection />
      <RoutesSection />
      <WhyChooseSection />
      <TestimonialsSection />
      <BookingSection />
      <FAQSection />
      <Footer />
      <WhatsAppFloat />
    </div>
  );
};

export default NaplesAirportPage;

